import type { ID } from "@/lib/catalog/types";
import { refreshStoredAuthUser } from "@/lib/authUser";
import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";
import { findEmployeeAllowanceTypeIdByLabel, getEmployeeAllowanceTypeById } from "@/lib/hr/employeeAllowanceTypesStore";
import { findEmployeeDeductionTypeIdByLabel, getEmployeeDeductionTypeById } from "@/lib/hr/employeeDeductionTypesStore";

export type EmployeeOffDayCode = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export const EMPLOYEE_OFF_DAY_OPTIONS: Array<{ id: EmployeeOffDayCode; short: string }> = [
  { id: "mon", short: "Mon" },
  { id: "tue", short: "Tue" },
  { id: "wed", short: "Wed" },
  { id: "thu", short: "Thu" },
  { id: "fri", short: "Fri" },
  { id: "sat", short: "Sat" },
  { id: "sun", short: "Sun" },
];

const LEGACY_WEEKDAY_TO_CODE: Record<number, EmployeeOffDayCode> = {
  0: "sun",
  1: "mon",
  2: "tue",
  3: "wed",
  4: "thu",
  5: "fri",
  6: "sat",
};

const VALID_OFF_DAY_CODES = new Set<EmployeeOffDayCode>(EMPLOYEE_OFF_DAY_OPTIONS.map((d) => d.id));

export function toggleEmployeeOffDay(days: EmployeeOffDayCode[], day: EmployeeOffDayCode): EmployeeOffDayCode[] {
  const next = days.includes(day) ? days.filter((d) => d !== day) : [...days, day];
  return EMPLOYEE_OFF_DAY_OPTIONS.map((d) => d.id).filter((id) => next.includes(id));
}

export type EmployeeGender = "male" | "female";

export interface EmployeeAllowance {
  id: string;
  /** Reference to allowance type catalog (from Employee allowance types). */
  allowanceTypeId: string;
  /** Denormalized label for API / legacy rows without type id */
  name: string;
  amount: string;
}

/** Payroll deduction line (e.g. PF, tax) — name + amount from API. */
export interface EmployeeDeduction {
  id: string;
  /** Reference to deduction type catalog (from Employee deduction types). */
  deductionTypeId: string;
  name: string;
  amount: string;
}

export interface EmployeeRecord {
  id: string;
  /** Linked app user (created when employee is added). */
  userId: string;
  employeeCode: string;
  /** ZKTeco device uid after successful biometric sync. */
  deviceUid: string | null;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  dateOfBirth: string;
  primaryContact: string;
  gender: EmployeeGender;
  cnic: string;
  /** Single field for father or husband name */
  fatherHusbandName: string;
  presentAddressLine1: string;
  presentAddressLine2: string;
  presentCity: string;
  presentState: string;
  presentCountry: string;
  permanentAddressLine1: string;
  permanentAddressLine2: string;
  permanentCity: string;
  permanentState: string;
  permanentCountry: string;
  /** Base salary before allowances (API `basic_salary`). */
  basicSalary: string;
  allowances: EmployeeAllowance[];
  deductions: EmployeeDeduction[];
  departmentId: string;
  departmentName: string;
  designationId: string;
  designationName: string;
  employeeType: string;
  payrollModel: "monthly" | "hourly";
  employeeStatus: string;
  paymentMethodType: string;
  bankName: string;
  bankAccountNumber: string;
  accountHolderName: string;
  joiningDate: string;
  confirmationDate: string;
  grade: string;
  payScale: string;
  minSalary: string;
  maxSalary: string;
  reportingTo: string;
  /** Assigned leave period (active) id, when returned by API. */
  leaveYearId: string;
  /** Assigned leave policy id (for active leave period), when returned by API. */
  leavePolicyId: string;
  /** Current assigned work shift id (active), when returned by API. */
  workShiftId: string;
  /** Weekly off days for this employee (e.g. mon, sat). */
  employeeOffDays: EmployeeOffDayCode[];
  /** Assigned work policy id (optional). */
  workPolicyId: string;
  /** Denormalized name from API `work_policy.name` for display when policy is not in assignable list. */
  workPolicyName: string;
  /** Assigned salary policy id (optional). */
  salaryPolicyId: string;
  /** Denormalized name from API `salary_policy.name` for display when policy is not in assignable list. */
  salaryPolicyName: string;
  workShiftAssignments: Array<{
    workShiftId: string;
    workShiftName: string;
    workShiftSlug: string;
    startTime: string;
    endTime: string;
    breakMinutes: number;
    crossesMidnight: boolean;
    active: boolean;
    effectiveFrom: string;
    effectiveTo: string;
  }>;
  leavePolicyAssignments: Array<{
    leaveYearId: string;
    leaveYearLabel: string;
    leaveYearStartDate: string;
    leaveYearEndDate: string;
    leaveYearActive: boolean;
    leavePolicyId: string;
    leavePolicyName: string;
    carryForward: boolean;
    encashmentAllowed: boolean;
  }>;
  leaveAllocations: Array<{
    leaveYearId: string;
    leaveYearLabel: string;
    leaveYearStartDate: string;
    leaveYearEndDate: string;
    leaveYearActive: boolean;
    leaveTypeId: string;
    leaveTypeTitle: string;
    leaveTypeSlug: string;
    totalDays: number;
    usedDays: number;
    remainingDays: number;
    carriedForwardDays: number;
    encashedDays: number;
  }>;
  /** Public URL from API (`photoDataUrl`) or data URL after local upload preview */
  photoDataUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export type EmployeeDraft = Omit<EmployeeRecord, "id" | "createdAt" | "updatedAt">;

const LS_KEY = "inventory.employees.v1";

type EmployeeApiRecord = {
  id?: number | string;
  user_id?: number | string | null;
  device_uid?: string | number | null;
  employee_code?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  email?: string | null;
  date_of_birth?: string | null;
  primary_contact?: string | null;
  gender?: string | null;
  cnic?: string | null;
  basic_salary?: string | number | null;
  /** @deprecated Old API / cached payloads */
  gross_pay?: string | number | null;
  address?: string | null;
  permanent_address?: string | null;
  father_husband_name?: string | null;
  /** @deprecated API legacy */
  father_name?: string | null;
  /** @deprecated API legacy */
  husband_name?: string | null;
  department_id?: string | number | null;
  department?: { id?: string | number; name?: string } | string | null;
  department_name?: string | null;
  designation_id?: string | number | null;
  designation?: { id?: string | number; name?: string } | string | null;
  designation_name?: string | null;
  employee_type?: string | null;
  payroll_model?: string | null;
  employee_status?: string | null;
  country?: string | null;
  city?: string | null;
  present_address_line1?: string | null;
  present_address_line2?: string | null;
  present_city?: string | null;
  present_state?: string | null;
  present_country?: string | null;
  permanent_address_line1?: string | null;
  permanent_address_line2?: string | null;
  permanent_city?: string | null;
  permanent_state?: string | null;
  permanent_country?: string | null;
  payment_method_type?: string | null;
  bank_name?: string | null;
  bank_account_number?: string | null;
  account_holder_name?: string | null;
  joining_date?: string | null;
  confirmation_date?: string | null;
  grade?: string | null;
  pay_scale?: string | null;
  employee_scale?: string | null;
  min_salary?: string | number | null;
  max_salary?: string | number | null;
  reporting_to?: string | null;
  leave_year_id?: number | string | null;
  leave_policy_id?: number | string | null;
  work_shift_id?: number | string | null;
  employee_off_days?: unknown;
  work_policy_id?: number | string | null;
  work_policy?: { id?: number | string; name?: string | null } | null;
  salary_policy_id?: number | string | null;
  salary_policy?: { id?: number | string; name?: string | null } | null;
  work_shift_assignments?: Array<{
    work_shift_id?: number | string | null;
    work_shift?: {
      id?: number | string;
      name?: string | null;
      slug?: string | null;
      start_time?: string | null;
      end_time?: string | null;
      break_minutes?: number | string | null;
      crosses_midnight?: boolean | null;
      active?: boolean | null;
    } | null;
    effective_from?: string | null;
    effective_to?: string | null;
  }> | null;
  leave_policy_assignments?: Array<{
    leave_year_id?: number | string;
    leave_year?: { id?: number | string; label?: string | null; start_date?: string | null; end_date?: string | null; active?: boolean } | null;
    leave_policy_id?: number | string;
    leave_policy?: { id?: number | string; name?: string | null; carry_forward?: boolean; encashment_allowed?: boolean } | null;
  }> | null;
  leave_allocations?: Array<{
    leave_year_id?: number | string;
    leave_year?: { id?: number | string; label?: string | null; start_date?: string | null; end_date?: string | null; active?: boolean } | null;
    leave_type_id?: number | string;
    leave_type?: { id?: number | string; title?: string | null; slug?: string | null } | null;
    total_days?: number | string | null;
    used_days?: number | string | null;
    remaining_days?: number | string | null;
    carried_forward_days?: number | string | null;
    encashed_days?: number | string | null;
  }> | null;
  allowances?: Array<{
    allowance_id?: string | number | null;
    name?: string | null;
    amount?: string | number | null;
  }> | null;
  deductions?: Array<{
    deduction_id?: string | number | null;
    name?: string | null;
    amount?: string | number | null;
  }> | null;
  photo_path?: string | null;
  photoDataUrl?: string | null;
  photo_data_url?: string | null;
  photo_url?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type UpsertEmployeeApiResponse = { message?: string; data?: EmployeeApiRecord };
type DeleteEmployeeApiResponse = { message?: string };

export type UpsertEmployeeResult = { record: EmployeeRecord; message: string };
export type DeleteEmployeeResult = { success: boolean; message: string };

/** Laravel-style validation / API errors for employee CRUD toasts. */
export function employeeApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Please try again.";
}

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function normalizeEmployeeOffDays(value: unknown): EmployeeOffDayCode[] {
  if (!Array.isArray(value)) return [];

  const codes = new Set<EmployeeOffDayCode>();
  for (const item of value) {
    if (typeof item === "string") {
      const code = item.trim().toLowerCase();
      if (VALID_OFF_DAY_CODES.has(code as EmployeeOffDayCode)) {
        codes.add(code as EmployeeOffDayCode);
      }
      continue;
    }

    const n = Number(item);
    if (Number.isInteger(n) && n >= 0 && n <= 6) {
      codes.add(LEGACY_WEEKDAY_TO_CODE[n]);
    }
  }

  return EMPLOYEE_OFF_DAY_OPTIONS.map((d) => d.id).filter((id) => codes.has(id));
}

/** Split legacy composed blocks (street lines + optional last line "city, state, country") without duplicating region on re-save. */
function parseLegacyAddressBlock(block: string, fallbackCity: string, fallbackCountry: string): {
  line1: string;
  line2: string;
  city: string;
  state: string;
  country: string;
} {
  const fbCity = fallbackCity.trim();
  const fbCountry = fallbackCountry.trim();
  const raw = block.trim();
  if (!raw) {
    return { line1: "", line2: "", city: fbCity, state: "", country: fbCountry };
  }
  const lines = raw
    .split(/\r\n|\r|\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) {
    return { line1: "", line2: "", city: fbCity, state: "", country: fbCountry };
  }
  const last = lines[lines.length - 1];
  const commaParts = last.split(",").map((p) => p.trim());
  if (commaParts.length >= 3) {
    const country = commaParts.pop() ?? "";
    const state = commaParts.pop() ?? "";
    const city = commaParts.join(", ");
    const streetLines = lines.slice(0, -1);
    const line1 = streetLines[0] ?? "";
    const line2 = streetLines.slice(1).join("\n");
    return {
      line1,
      line2,
      city: city || fbCity,
      state,
      country: country || fbCountry,
    };
  }
  const line1 = lines[0] ?? "";
  const line2 = lines.slice(1).join("\n");
  return { line1, line2, city: fbCity, state: "", country: fbCountry };
}

function hasStructuredPresentFields(record: EmployeeApiRecord): boolean {
  const keys: (keyof EmployeeApiRecord)[] = [
    "present_address_line1",
    "present_address_line2",
    "present_city",
    "present_state",
    "present_country",
  ];
  return keys.some((k) => str(record[k]).trim() !== "");
}

function hasStructuredPermanentFields(record: EmployeeApiRecord): boolean {
  const keys: (keyof EmployeeApiRecord)[] = [
    "permanent_address_line1",
    "permanent_address_line2",
    "permanent_city",
    "permanent_state",
    "permanent_country",
  ];
  return keys.some((k) => str(record[k]).trim() !== "");
}

function normalizeGender(raw: string | null | undefined): EmployeeGender {
  const g = (raw ?? "").trim().toLowerCase();
  if (g === "female" || g === "f") return "female";
  return "male";
}

function dedupeById(items: EmployeeRecord[]): EmployeeRecord[] {
  const byId = new Map<ID, EmployeeRecord>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function extractRowsFromResponse(body: unknown): EmployeeApiRecord[] {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const fromBlock = (block: unknown): EmployeeApiRecord[] | null => {
    if (Array.isArray(block)) return block as EmployeeApiRecord[];
    if (block && typeof block === "object" && !Array.isArray(block)) {
      const inner = (block as Record<string, unknown>).data;
      if (Array.isArray(inner)) return inner as EmployeeApiRecord[];
    }
    return null;
  };
  for (const key of ["data", "employees", "items", "results"]) {
    const hit = fromBlock(root[key]);
    if (hit) return hit;
  }
  return [];
}

function readLastPageFromListBody(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as Record<string, unknown>).meta;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    const lp = (meta as Record<string, unknown>).last_page;
    if (lp == null) return null;
    const n = typeof lp === "number" ? lp : Number(lp);
    if (Number.isFinite(n) && n >= 1) return n;
  }
  return null;
}

function toPhotoUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("data:")) return s;
  return s.startsWith("/") ? s : `/${s}`;
}

function resolveEmployeePhotoFromApi(record: EmployeeApiRecord): string | null {
  const loose = record as Record<string, unknown>;
  const candidates = [
    record.photoDataUrl,
    record.photo_data_url,
    record.photo_url,
    loose.photoUrl,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim() !== "") {
      return toPhotoUrl(c.trim());
    }
  }
  return toPhotoUrl(record.photo_path);
}

function extractEmployeeFromUpsertBody(body: unknown): EmployeeApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const block = data as Record<string, unknown>;
    if ("id" in block || block.first_name != null || block.last_name != null) {
      return block as unknown as EmployeeApiRecord;
    }
    const nested = block.employee;
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      return nested as unknown as EmployeeApiRecord;
    }
  }
  const top = root.employee;
  if (top && typeof top === "object" && !Array.isArray(top)) {
    return top as unknown as EmployeeApiRecord;
  }
  if ("id" in root && root.id != null) {
    return root as unknown as EmployeeApiRecord;
  }
  return null;
}

function deptIdName(record: EmployeeApiRecord): { id: string; name: string } {
  if (record.department && typeof record.department === "object") {
    const o = record.department as { id?: string | number; name?: string };
    return { id: str(o.id), name: str(o.name) };
  }
  return { id: str(record.department_id), name: str(record.department_name) };
}

function desigIdName(record: EmployeeApiRecord): { id: string; name: string } {
  if (record.designation && typeof record.designation === "object") {
    const o = record.designation as { id?: string | number; name?: string };
    return { id: str(o.id), name: str(o.name) };
  }
  return { id: str(record.designation_id), name: str(record.designation_name) };
}

function mapAllowancesFromApi(raw: EmployeeApiRecord["allowances"]): EmployeeAllowance[] {
  if (!raw || !Array.isArray(raw)) return [];
  return raw.map((a, idx) => {
    const name = str(a?.name);
    const typeIdFromApi = str(a?.allowance_id ?? (a as { employee_allowance_type_id?: unknown }).employee_allowance_type_id);
    const allowanceTypeId = typeIdFromApi || findEmployeeAllowanceTypeIdByLabel(name);
    const resolvedName =
      name.trim() ||
      (allowanceTypeId.trim() ? str(getEmployeeAllowanceTypeById(allowanceTypeId.trim())?.label) : "");
    return {
      id: `allow-api-${idx}-${Date.now()}`,
      allowanceTypeId,
      name: resolvedName,
      amount: str(a?.amount).replace(/,/g, ""),
    };
  });
}

function mapDeductionsFromApi(raw: EmployeeApiRecord["deductions"]): EmployeeDeduction[] {
  if (!raw || !Array.isArray(raw)) return [];
  return raw.map((row, idx) => {
    const name = str(row?.name);
    const typeIdFromApi = str(
      row?.deduction_id ?? (row as { employee_deduction_type_id?: unknown }).employee_deduction_type_id,
    );
    const deductionTypeId = typeIdFromApi || findEmployeeDeductionTypeIdByLabel(name);
    const resolvedName =
      name.trim() ||
      (deductionTypeId.trim() ? str(getEmployeeDeductionTypeById(deductionTypeId.trim())?.label) : "");
    return {
      id: `deduct-api-${idx}`,
      deductionTypeId,
      name: resolvedName,
      amount: str(row?.amount).replace(/,/g, ""),
    };
  });
}

function mapApiRecord(record: EmployeeApiRecord): EmployeeRecord {
  const created = record.created_at || new Date().toISOString();
  const dept = deptIdName(record);
  const desig = desigIdName(record);
  const scale = str(record.pay_scale || record.employee_scale);
  const first = str(record.first_name);
  const last = str(record.last_name);
  const fullFromApi = str(record.full_name).trim();
  const fullName = fullFromApi || `${first} ${last}`.trim();
  const fromCombined = str(record.father_husband_name);
  const fa = str(record.father_name);
  const hu = str(record.husband_name);
  const fatherHusbandName = fromCombined || [fa, hu].filter((x) => x.trim()).join(" / ");
  const cityFb = str(record.city);
  const countryFb = str(record.country);

  let present: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    country: string;
  };
  if (hasStructuredPresentFields(record)) {
    present = {
      line1: str(record.present_address_line1),
      line2: str(record.present_address_line2),
      city: str(record.present_city),
      state: str(record.present_state),
      country: str(record.present_country),
    };
  } else {
    present = parseLegacyAddressBlock(str(record.address), cityFb, countryFb);
  }

  let permanent: typeof present;
  if (hasStructuredPermanentFields(record)) {
    permanent = {
      line1: str(record.permanent_address_line1),
      line2: str(record.permanent_address_line2),
      city: str(record.permanent_city),
      state: str(record.permanent_state),
      country: str(record.permanent_country),
    };
  } else if (str(record.permanent_address).trim() !== "") {
    permanent = parseLegacyAddressBlock(str(record.permanent_address), cityFb, countryFb);
  } else {
    permanent = { line1: "", line2: "", city: "", state: "", country: "" };
  }

  const leavePolicyAssignments = Array.isArray(record.leave_policy_assignments)
    ? record.leave_policy_assignments.map((a, idx) => {
        const year = a?.leave_year ?? null;
        const policy = a?.leave_policy ?? null;
        return {
          leaveYearId: str(a?.leave_year_id) || str(year?.id) || `year-${idx}`,
          leaveYearLabel: str(year?.label),
          leaveYearStartDate: str(year?.start_date).slice(0, 10),
          leaveYearEndDate: str(year?.end_date).slice(0, 10),
          leaveYearActive: Boolean(year?.active),
          leavePolicyId: str(a?.leave_policy_id) || str(policy?.id),
          leavePolicyName: str(policy?.name),
          carryForward: Boolean(policy?.carry_forward),
          encashmentAllowed: Boolean(policy?.encashment_allowed),
        };
      })
    : [];

  const workShiftAssignments = Array.isArray(record.work_shift_assignments)
    ? record.work_shift_assignments.map((a, idx) => {
        const shift = a?.work_shift ?? null;
        return {
          workShiftId: str(a?.work_shift_id) || str(shift?.id) || `shift-${idx}`,
          workShiftName: str(shift?.name),
          workShiftSlug: str(shift?.slug),
          startTime: str(shift?.start_time),
          endTime: str(shift?.end_time),
          breakMinutes: Number(shift?.break_minutes ?? 0) || 0,
          crossesMidnight: Boolean(shift?.crosses_midnight),
          active: shift?.active !== false,
          effectiveFrom: str(a?.effective_from).slice(0, 10),
          effectiveTo: str(a?.effective_to).slice(0, 10),
        };
      })
    : [];

  const leaveAllocations = Array.isArray(record.leave_allocations)
    ? record.leave_allocations.map((a, idx) => {
        const year = a?.leave_year ?? null;
        const type = a?.leave_type ?? null;
        const title = str(type?.title);
        const slug = str(type?.slug);
        const used = Number(a?.used_days ?? 0) || 0;
        const remaining = Number(a?.remaining_days ?? 0) || 0;
        const total = Number(a?.total_days ?? 0) || 0;
        const carriedForwardDays = Number(a?.carried_forward_days ?? 0) || 0;
        const encashedDays = Number(a?.encashed_days ?? 0) || 0;
        return {
          leaveYearId: str(a?.leave_year_id) || str(year?.id) || "",
          leaveYearLabel: str(year?.label),
          leaveYearStartDate: str(year?.start_date).slice(0, 10),
          leaveYearEndDate: str(year?.end_date).slice(0, 10),
          leaveYearActive: Boolean(year?.active),
          leaveTypeId: str(a?.leave_type_id) || str(type?.id) || `alloc-${idx}`,
          leaveTypeTitle: title,
          leaveTypeSlug: slug,
          totalDays: total,
          usedDays: used,
          remainingDays: remaining,
          carriedForwardDays,
          encashedDays,
        };
      })
    : [];

  return {
    id: String(record.id ?? ""),
    userId: record.user_id != null && record.user_id !== "" ? String(record.user_id) : "",
    employeeCode: str(record.employee_code).trim(),
    deviceUid: record.device_uid != null && String(record.device_uid).trim() !== "" ? String(record.device_uid).trim() : null,
    firstName: first,
    lastName: last,
    fullName,
    email: str(record.email),
    dateOfBirth: str(record.date_of_birth),
    primaryContact: str(record.primary_contact),
    gender: normalizeGender(record.gender ?? undefined),
    cnic: str(record.cnic),
    fatherHusbandName: fatherHusbandName || fa || hu,
    presentAddressLine1: present.line1,
    presentAddressLine2: present.line2,
    presentCity: present.city,
    presentState: present.state,
    presentCountry: present.country,
    permanentAddressLine1: permanent.line1,
    permanentAddressLine2: permanent.line2,
    permanentCity: permanent.city,
    permanentState: permanent.state,
    permanentCountry: permanent.country,
    basicSalary: str(record.basic_salary ?? record.gross_pay).replace(/,/g, ""),
    allowances: mapAllowancesFromApi(record.allowances),
    deductions: mapDeductionsFromApi(record.deductions),
    departmentId: dept.id,
    departmentName: dept.name,
    designationId: desig.id,
    designationName: desig.name,
    employeeType: str(record.employee_type),
    payrollModel: record.payroll_model === "hourly" ? "hourly" : "monthly",
    employeeStatus: str(record.employee_status),
    paymentMethodType: str(record.payment_method_type),
    bankName: str(record.bank_name),
    bankAccountNumber: str(record.bank_account_number),
    accountHolderName: str(record.account_holder_name),
    joiningDate: str(record.joining_date),
    confirmationDate: str(record.confirmation_date),
    grade: str(record.grade),
    payScale: scale,
    minSalary: str(record.min_salary),
    maxSalary: str(record.max_salary),
    reportingTo: str(record.reporting_to),
    leaveYearId: str(record.leave_year_id),
    leavePolicyId: str(record.leave_policy_id),
    workShiftId: str(record.work_shift_id),
    employeeOffDays: normalizeEmployeeOffDays(record.employee_off_days),
    workPolicyId:
      record.work_policy_id != null && record.work_policy_id !== ""
        ? str(record.work_policy_id)
        : record.work_policy?.id != null
          ? str(record.work_policy.id)
          : "",
    workPolicyName: record.work_policy?.name != null ? str(record.work_policy.name) : "",
    salaryPolicyId:
      record.salary_policy_id != null && record.salary_policy_id !== ""
        ? str(record.salary_policy_id)
        : record.salary_policy?.id != null
          ? str(record.salary_policy.id)
          : "",
    salaryPolicyName: record.salary_policy?.name != null ? str(record.salary_policy.name) : "",
    workShiftAssignments,
    leavePolicyAssignments,
    leaveAllocations,
    photoDataUrl: resolveEmployeePhotoFromApi(record),
    createdAt: created,
    updatedAt: str(record.updated_at) || created,
  };
}

function buildApiPayload(draft: EmployeeDraft) {
  const n = (s: string | undefined | null) => {
    const t = String(s ?? "").trim();
    return t === "" ? null : t;
  };
  const allowancesPayload = draft.allowances
    .map((a) => {
      const type = a.allowanceTypeId.trim() ? getEmployeeAllowanceTypeById(a.allowanceTypeId.trim()) : undefined;
      const typeIdNum = type?.id != null ? Number(type.id) : Number(a.allowanceTypeId);
      if (!Number.isFinite(typeIdNum) || typeIdNum < 1) return null;
      const amount = a.amount.replace(/[^\d.]/g, "") || "0";
      return { allowance_id: typeIdNum, amount };
    })
    .filter((row): row is { allowance_id: number; amount: string } => row != null);

  const deductionsPayload = draft.deductions
    .map((d) => {
      const type = d.deductionTypeId.trim() ? getEmployeeDeductionTypeById(d.deductionTypeId.trim()) : undefined;
      const typeIdNum = type?.id != null ? Number(type.id) : Number(d.deductionTypeId);
      if (!Number.isFinite(typeIdNum) || typeIdNum < 1) return null;
      const amount = d.amount.replace(/[^\d.]/g, "") || "0";
      return { deduction_id: typeIdNum, amount };
    })
    .filter((row): row is { deduction_id: number; amount: string } => row != null);

  return {
    employee_code: n(draft.employeeCode),
    first_name: String(draft.firstName ?? "").trim(),
    last_name: String(draft.lastName ?? "").trim(),
    full_name: n(draft.fullName),
    email: n(draft.email),
    date_of_birth: n(draft.dateOfBirth),
    primary_contact: String(draft.primaryContact ?? "").trim(),
    gender: draft.gender,
    cnic: n(draft.cnic),
    basic_salary: n(draft.basicSalary)?.replace(/,/g, "") || null,
    present_address_line1: n(draft.presentAddressLine1),
    present_address_line2: n(draft.presentAddressLine2),
    present_city: n(draft.presentCity),
    present_state: n(draft.presentState),
    present_country: n(draft.presentCountry),
    permanent_address_line1: n(draft.permanentAddressLine1),
    permanent_address_line2: n(draft.permanentAddressLine2),
    permanent_city: n(draft.permanentCity),
    permanent_state: n(draft.permanentState),
    permanent_country: n(draft.permanentCountry),
    father_husband_name: n(draft.fatherHusbandName),
    department_id: n(draft.departmentId),
    department_name: n(draft.departmentName),
    designation_id: n(draft.designationId),
    designation_name: n(draft.designationName),
    employee_type: n(draft.employeeType),
    payroll_model: draft.payrollModel,
    employee_status: n(draft.employeeStatus),
    payment_method_type: n(draft.paymentMethodType),
    bank_name: n(draft.bankName),
    bank_account_number: n(draft.bankAccountNumber),
    account_holder_name: n(draft.accountHolderName),
    joining_date: n(draft.joiningDate),
    confirmation_date: n(draft.confirmationDate),
    grade: n(draft.grade),
    pay_scale: n(draft.payScale),
    min_salary: n(draft.minSalary),
    max_salary: n(draft.maxSalary),
    reporting_to: n(draft.reportingTo),
    leave_policy_id: n(draft.leavePolicyId),
    leave_year_id: n(draft.leaveYearId),
    work_shift_id: n(draft.workShiftId),
    work_shift_effective_from: n(draft.joiningDate),
    employee_off_days: draft.employeeOffDays.length ? draft.employeeOffDays : null,
    work_policy_id: (() => {
      const t = String(draft.workPolicyId ?? "").trim();
      if (t === "") return null;
      const num = Number(t);
      return Number.isFinite(num) && num >= 1 ? num : null;
    })(),
    salary_policy_id: (() => {
      const t = String(draft.salaryPolicyId ?? "").trim();
      if (t === "") return null;
      const num = Number(t);
      return Number.isFinite(num) && num >= 1 ? num : null;
    })(),
    allowances: allowancesPayload.length ? allowancesPayload : null,
    deductions: deductionsPayload.length ? deductionsPayload : null,
  };
}

/** Multipart file field name expected by the API (`$request->file('photo')`). */
const EMPLOYEE_PHOTO_FORM_KEY = "photo";

function appendEmployeePhotoToFormData(form: FormData, photoFile: Blob) {
  const name =
    photoFile instanceof File && photoFile.name.trim()
      ? photoFile.name
      : "employee-photo.jpg";
  form.append(EMPLOYEE_PHOTO_FORM_KEY, photoFile, name);
}

/** Ensures multipart employee saves still sync legacy `address` / `city` columns (see `EmployeeService::syncLegacyAddressColumnsFromStructured`). */
const STRUCTURED_ADDRESS_FORM_KEYS = new Set([
  "present_address_line1",
  "present_address_line2",
  "present_city",
  "present_state",
  "present_country",
  "permanent_address_line1",
  "permanent_address_line2",
  "permanent_city",
  "permanent_state",
  "permanent_country",
]);

function appendEmployeePayloadToFormData(form: FormData, payload: Record<string, unknown>) {
  for (const [key, value] of Object.entries(payload)) {
    if (value === null || value === undefined) {
      if (STRUCTURED_ADDRESS_FORM_KEYS.has(key)) {
        form.append(key, "");
      } else if (key === "work_policy_id") {
        form.append("work_policy_id", "");
      } else if (key === "salary_policy_id") {
        form.append("salary_policy_id", "");
      }
      continue;
    }
    // Nested multipart arrays are unreliable with some PHP stacks; JSON matches `productsStore` multipart pattern.
    if (key === "allowances" && Array.isArray(value)) {
      form.append("allowances", JSON.stringify(value));
      continue;
    }
    if (key === "deductions" && Array.isArray(value)) {
      form.append("deductions", JSON.stringify(value));
      continue;
    }
    if (key === "role_names" && Array.isArray(value)) {
      form.append("role_names", JSON.stringify(value));
      continue;
    }
    if (key === "permission_names" && Array.isArray(value)) {
      form.append("permission_names", JSON.stringify(value));
      continue;
    }
    form.append(key, String(value));
  }
}

export type EmployeeUpsertOptions = {
  /** When set, request is sent as `multipart/form-data` with this file under `photo`. */
  photoFile?: File | null;
  /** Spatie roles + direct permissions for the linked user (employee update only). */
  access?: {
    roleNames: string[];
    permissionNames: string[];
  };
};

function coerceEmployee(raw: unknown): EmployeeRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (!r.id) return null;
  const base = emptyEmployeeDraft();

  const legacyFather = [str(r.fatherName), str(r.husbandName)].filter((x) => x.trim()).join(" / ");
  const fatherHusbandName = str(r.fatherHusbandName) || str(r.father_husband_name) || legacyFather;

  const presentLine1 = str(r.presentAddressLine1) || str(r.address);
  const permLine1 = str(r.permanentAddressLine1) || str(r.permanentAddress);
  const presentCity = str(r.presentCity) || str(r.city);
  const presentCountry = str(r.presentCountry) || str(r.country);

  let allowances: EmployeeAllowance[] = base.allowances;
  if (Array.isArray(r.allowances)) {
    allowances = (r.allowances as unknown[]).map((item, idx) => {
      const a = item as {
        id?: string;
        allowanceTypeId?: string;
        allowance_id?: string | number;
        employee_allowance_type_id?: string | number;
        name?: string;
        amount?: string;
      };
      const allowanceTypeId = str(a.allowanceTypeId) || str(a.allowance_id) || str(a.employee_allowance_type_id);
      return {
        id: str(a.id) || `allow-${idx}-${typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(idx)}`,
        allowanceTypeId,
        name: str(a.name),
        amount: str(a.amount).replace(/,/g, ""),
      };
    });
  }

  let deductions: EmployeeDeduction[] = base.deductions;
  if (Array.isArray(r.deductions)) {
    deductions = (r.deductions as unknown[]).map((item, idx) => {
      const d = item as {
        id?: string;
        deductionTypeId?: string;
        deduction_id?: string | number;
        employee_deduction_type_id?: string | number;
        name?: string;
        amount?: string;
      };
      const deductionTypeId = str(d.deductionTypeId) || str(d.deduction_id) || str(d.employee_deduction_type_id);
      return {
        id: str(d.id) || `deduct-${idx}-${typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(idx)}`,
        deductionTypeId,
        name: str(d.name),
        amount: str(d.amount).replace(/,/g, ""),
      };
    });
  }

  return {
    id: str(r.id),
    userId: str(r.userId),
    employeeCode: str(r.employeeCode),
    deviceUid:
      (r as { deviceUid?: unknown }).deviceUid != null && String((r as { deviceUid?: unknown }).deviceUid).trim() !== ""
        ? String((r as { deviceUid?: unknown }).deviceUid).trim()
        : null,
    firstName: str(r.firstName),
    lastName: str(r.lastName),
    fullName: str(r.fullName),
    email: str(r.email),
    dateOfBirth: str(r.dateOfBirth),
    primaryContact: str(r.primaryContact),
    gender: r.gender === "female" ? "female" : "male",
    cnic: str(r.cnic),
    fatherHusbandName,
    presentAddressLine1: presentLine1,
    presentAddressLine2: str(r.presentAddressLine2),
    presentCity,
    presentState: str(r.presentState),
    presentCountry,
    permanentAddressLine1: permLine1,
    permanentAddressLine2: str(r.permanentAddressLine2),
    permanentCity: str(r.permanentCity),
    permanentState: str(r.permanentState),
    permanentCountry: str(r.permanentCountry) || presentCountry,
    basicSalary: str(r["basicSalary"] ?? r["grossPay"]).replace(/,/g, ""),
    allowances,
    deductions,
    departmentId: str(r.departmentId),
    departmentName: str(r.departmentName),
    designationId: str(r.designationId),
    designationName: str(r.designationName),
    employeeType: str(r.employeeType),
    employeeStatus: str(r.employeeStatus),
    paymentMethodType: str(r.paymentMethodType),
    bankName: str(r.bankName),
    bankAccountNumber: str(r.bankAccountNumber),
    accountHolderName: str(r.accountHolderName),
    joiningDate: str(r.joiningDate),
    confirmationDate: str(r.confirmationDate),
    grade: str(r.grade),
    payScale: str(r.payScale),
    minSalary: str(r.minSalary),
    maxSalary: str(r.maxSalary),
    reportingTo: str(r.reportingTo),
    leaveYearId: str(r.leaveYearId),
    leavePolicyId: str(r.leavePolicyId),
    workShiftId: str((r as { workShiftId?: unknown }).workShiftId),
    employeeOffDays: normalizeEmployeeOffDays((r as { employeeOffDays?: unknown }).employeeOffDays),
    workPolicyId: str((r as { workPolicyId?: unknown }).workPolicyId),
    workPolicyName: str((r as { workPolicyName?: unknown }).workPolicyName),
    salaryPolicyId: str((r as { salaryPolicyId?: unknown }).salaryPolicyId),
    salaryPolicyName: str((r as { salaryPolicyName?: unknown }).salaryPolicyName),
    payrollModel: (r as { payrollModel?: string }).payrollModel === "hourly" ? "hourly" : "monthly",
    workShiftAssignments: Array.isArray((r as { workShiftAssignments?: unknown }).workShiftAssignments)
      ? ((r as { workShiftAssignments?: unknown }).workShiftAssignments as unknown[]).map((row, idx) => {
          const a = row as {
            workShiftId?: string;
            workShiftName?: string;
            workShiftSlug?: string;
            startTime?: string;
            endTime?: string;
            breakMinutes?: number;
            crossesMidnight?: boolean;
            active?: boolean;
            effectiveFrom?: string;
            effectiveTo?: string;
          };
          return {
            workShiftId: str(a.workShiftId) || `shift-${idx}`,
            workShiftName: str(a.workShiftName),
            workShiftSlug: str(a.workShiftSlug),
            startTime: str(a.startTime),
            endTime: str(a.endTime),
            breakMinutes: Number(a.breakMinutes ?? 0) || 0,
            crossesMidnight: Boolean(a.crossesMidnight),
            active: a.active !== false,
            effectiveFrom: str(a.effectiveFrom).slice(0, 10),
            effectiveTo: str(a.effectiveTo).slice(0, 10),
          };
        })
      : base.workShiftAssignments,
    leavePolicyAssignments: Array.isArray(r.leavePolicyAssignments)
      ? (r.leavePolicyAssignments as unknown[]).map((row, idx) => {
          const a = row as {
            leaveYearId?: string;
            leaveYearLabel?: string;
            leaveYearStartDate?: string;
            leaveYearEndDate?: string;
            leaveYearActive?: boolean;
            leavePolicyId?: string;
            leavePolicyName?: string;
            carryForward?: boolean;
            encashmentAllowed?: boolean;
          };
          return {
            leaveYearId: str(a.leaveYearId) || `year-${idx}`,
            leaveYearLabel: str(a.leaveYearLabel),
            leaveYearStartDate: str(a.leaveYearStartDate).slice(0, 10),
            leaveYearEndDate: str(a.leaveYearEndDate).slice(0, 10),
            leaveYearActive: Boolean(a.leaveYearActive),
            leavePolicyId: str(a.leavePolicyId),
            leavePolicyName: str(a.leavePolicyName),
            carryForward: Boolean(a.carryForward),
            encashmentAllowed: Boolean(a.encashmentAllowed),
          };
        })
      : base.leavePolicyAssignments,
    leaveAllocations: Array.isArray(r.leaveAllocations)
      ? (r.leaveAllocations as unknown[]).map((row, idx) => {
          const a = row as {
            leaveYearId?: string;
            leaveYearLabel?: string;
            leaveYearStartDate?: string;
            leaveYearEndDate?: string;
            leaveYearActive?: boolean;
            leaveTypeId?: string;
            leaveTypeTitle?: string;
            leaveTypeSlug?: string;
            totalDays?: number;
            usedDays?: number;
            remainingDays?: number;
            carriedForwardDays?: number;
            encashedDays?: number;
          };
          return {
            leaveYearId: str(a.leaveYearId),
            leaveYearLabel: str(a.leaveYearLabel),
            leaveYearStartDate: str(a.leaveYearStartDate).slice(0, 10),
            leaveYearEndDate: str(a.leaveYearEndDate).slice(0, 10),
            leaveYearActive: Boolean(a.leaveYearActive),
            leaveTypeId: str(a.leaveTypeId) || `alloc-${idx}`,
            leaveTypeTitle: str(a.leaveTypeTitle),
            leaveTypeSlug: str(a.leaveTypeSlug),
            totalDays: Number(a.totalDays ?? 0) || 0,
            usedDays: Number(a.usedDays ?? 0) || 0,
            remainingDays: Number(a.remainingDays ?? 0) || 0,
            carriedForwardDays: Number(a.carriedForwardDays ?? 0) || 0,
            encashedDays: Number(a.encashedDays ?? 0) || 0,
          };
        })
      : base.leaveAllocations,
    photoDataUrl: str(r.photoDataUrl).trim() || null,
    createdAt: str(r.createdAt),
    updatedAt: str(r.updatedAt),
  };
}

function load(): EmployeeRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const flat = JSON.parse(raw) as unknown[];
    if (!Array.isArray(flat)) return [];
    const merged = flat.map(coerceEmployee).filter(Boolean) as EmployeeRecord[];
    return dedupeById(merged);
  } catch {
    return [];
  }
}

function save(items: EmployeeRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

export function suggestNextEmployeeCode(employees: EmployeeRecord[]): string {
  let max = 0;
  for (const e of employees) {
    const m = /^EMP-(\d+)$/i.exec((e.employeeCode ?? "").trim());
    if (m) max = Math.max(max, parseInt(m[1], 10));
  }
  const next = max + 1;
  return `EMP-${String(next).padStart(5, "0")}`;
}

export function emptyEmployeeDraft(overrides: Partial<EmployeeDraft> = {}): EmployeeDraft {
  const base: EmployeeDraft = {
    userId: "",
    employeeCode: "",
    deviceUid: null,
    firstName: "",
    lastName: "",
    fullName: "",
    email: "",
    dateOfBirth: "",
    primaryContact: "",
    gender: "male",
    cnic: "",
    fatherHusbandName: "",
    presentAddressLine1: "",
    presentAddressLine2: "",
    presentCity: "",
    presentState: "",
    presentCountry: "Pakistan",
    permanentAddressLine1: "",
    permanentAddressLine2: "",
    permanentCity: "",
    permanentState: "",
    permanentCountry: "Pakistan",
    basicSalary: "",
    allowances: [],
    deductions: [],
    departmentId: "",
    departmentName: "",
    designationId: "",
    designationName: "",
    employeeType: "",
    payrollModel: "monthly",
    employeeStatus: "",
    paymentMethodType: "",
    bankName: "",
    bankAccountNumber: "",
    accountHolderName: "",
    joiningDate: "",
    confirmationDate: "",
    grade: "",
    payScale: "",
    minSalary: "",
    maxSalary: "",
    reportingTo: "",
    leaveYearId: "",
    leavePolicyId: "",
    workShiftId: "",
    employeeOffDays: [],
    workPolicyId: "",
    workPolicyName: "",
    salaryPolicyId: "",
    salaryPolicyName: "",
    workShiftAssignments: [],
    leavePolicyAssignments: [],
    leaveAllocations: [],
    photoDataUrl: null,
  };
  return { ...base, ...overrides };
}

export function recordToDraft(record: EmployeeRecord): EmployeeDraft {
  const { id: _id, createdAt: _c, updatedAt: _u, ...rest } = record;
  return { ...emptyEmployeeDraft(), ...rest };
}

export function listEmployees(): EmployeeRecord[] {
  return load().sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

/** Count employees that include an allowance row linked to this type id. */
export function countEmployeesUsingAllowanceType(allowanceTypeId: ID): number {
  let n = 0;
  for (const e of load()) {
    if (e.allowances.some((a) => a.allowanceTypeId === allowanceTypeId)) n += 1;
  }
  return n;
}

export function getEmployeeById(id: ID): EmployeeRecord | undefined {
  return load().find((e) => e.id === id);
}

const EMPLOYEES_PER_PAGE = 20;

export async function syncEmployeesFromApi(): Promise<EmployeeRecord[]> {
  try {
    const collected: EmployeeApiRecord[] = [];
    let page = 1;
    let lastPage = 1;
    do {
      const response = await api.get("/api/v1/employees", {
        params: { page, per_page: EMPLOYEES_PER_PAGE },
      });
      const body = response.data;
      collected.push(...extractRowsFromResponse(body));
      lastPage = readLastPageFromListBody(body) ?? 1;
      page += 1;
    } while (page <= lastPage && page <= 50);

    const records = dedupeById(
      collected.map(mapApiRecord).filter((r) => Boolean(r.id)),
    );
    save(records);
    return listEmployees();
  } catch (error) {
    // 403 is a normal case when backend permission middleware blocks access.
    if (!(isAxiosError(error) && error.response?.status === 403)) {
      console.error("[Employees] syncEmployeesFromApi failed", error);
    }
    throw error;
  }
}

export async function createEmployee(
  draft: EmployeeDraft,
  options?: EmployeeUpsertOptions,
): Promise<UpsertEmployeeResult> {
  const items = load();
  const payload = buildApiPayload(draft);
  const photoFile = options?.photoFile ?? null;

  let response: { data: UpsertEmployeeApiResponse };
  if (photoFile) {
    const form = new FormData();
    appendEmployeePayloadToFormData(form, payload as Record<string, unknown>);
    appendEmployeePhotoToFormData(form, photoFile);
    response = await api.post<UpsertEmployeeApiResponse>("/api/v1/employees", form);
  } else {
    response = await api.post<UpsertEmployeeApiResponse>("/api/v1/employees", payload);
  }

  const created = extractEmployeeFromUpsertBody(response.data);
  if (!created || created.id == null) {
    throw new Error("Invalid create employee response.");
  }
  const record = mapApiRecord(created);
  items.unshift(record);
  save(items);
  return {
    record,
    message: response.data?.message || "Employee created successfully.",
  };
}

export async function updateEmployee(
  id: ID,
  draft: EmployeeDraft,
  options?: EmployeeUpsertOptions,
): Promise<UpsertEmployeeResult | null> {
  const items = load();
  const idx = items.findIndex((e) => e.id === id);
  if (idx === -1) return null;
  const payload = buildApiPayload(draft) as Record<string, unknown>;
  const photoFile = options?.photoFile ?? null;
  if (options?.access && (draft.userId ?? "").trim()) {
    payload.guard_name = "web";
    payload.role_names = options.access.roleNames;
    payload.permission_names = options.access.permissionNames;
  }

  let response: { data: UpsertEmployeeApiResponse };
  if (photoFile) {
    const form = new FormData();
    appendEmployeePayloadToFormData(form, payload);
    form.append("_method", "PUT");
    appendEmployeePhotoToFormData(form, photoFile);
    response = await api.post<UpsertEmployeeApiResponse>(`/api/v1/employees/${id}`, form);
  } else {
    response = await api.put<UpsertEmployeeApiResponse>(`/api/v1/employees/${id}`, payload);
  }

  let updated = extractEmployeeFromUpsertBody(response.data);
  if (!updated) throw new Error("Invalid update employee response.");
  if (updated.id == null || String(updated.id) === "") {
    updated = { ...updated, id };
  }
  const record = mapApiRecord(updated);
  items[idx] = record;
  save(items);
  if (options?.access) void refreshStoredAuthUser();
  return { record, message: response.data?.message || "Employee updated successfully." };
}

export async function updateEmployeeCode(
  id: ID,
  employeeCode: string,
): Promise<{ record: EmployeeRecord; message: string; deviceSynced: boolean }> {
  const response = await api.patch<UpsertEmployeeApiResponse>(`/api/v1/employees/${id}/employee-code`, {
    employee_code: employeeCode.trim(),
  });

  let updated = extractEmployeeFromUpsertBody(response.data);
  if (!updated) throw new Error("Invalid update employee code response.");
  if (updated.id == null || String(updated.id) === "") {
    updated = { ...updated, id };
  }

  const record = mapApiRecord(updated);
  const items = load();
  const idx = items.findIndex((e) => e.id === id);
  if (idx !== -1) {
    items[idx] = record;
    save(items);
  }

  const message = response.data?.message || "Employee code updated.";
  const deviceSynced = /synced to biometric device/i.test(message);

  return { record, message, deviceSynced };
}

export async function deleteEmployee(id: ID): Promise<DeleteEmployeeResult> {
  const response = await api.delete<DeleteEmployeeApiResponse>(`/api/v1/employees/${id}`);
  const items = load();
  const next = items.filter((e) => e.id !== id);
  const removed = next.length !== items.length;
  if (removed) save(next);
  return {
    success: removed,
    message:
      response.data?.message ||
      (removed ? "Employee removed." : "Employee was not found."),
  };
}

type EnrollBiometricDeviceApiResponse = {
  message?: string;
  data?: {
    success?: boolean;
    ip_address?: string | null;
    uid?: number;
    device_uid?: string | number | null;
  };
};

export async function enrollEmployeeOnBiometricDevice(id: ID): Promise<{
  message: string;
  ipAddress: string | null;
  deviceUid: string | null;
}> {
  const response = await api.post<EnrollBiometricDeviceApiResponse>(`/api/v1/employees/${id}/enroll-biometric-device`);
  const deviceUidRaw = response.data?.data?.device_uid ?? response.data?.data?.uid;
  const deviceUid =
    deviceUidRaw != null && String(deviceUidRaw).trim() !== "" ? String(deviceUidRaw).trim() : null;

  if (deviceUid) {
    const items = load();
    const idx = items.findIndex((e) => String(e.id) === String(id));
    if (idx >= 0) {
      items[idx] = { ...items[idx], deviceUid };
      save(items);
    }
  }

  return {
    message: response.data?.message ?? "Employee enrolled on biometric device.",
    ipAddress: response.data?.data?.ip_address ?? null,
    deviceUid,
  };
}
